import React, {useContext, useEffect, useState} from 'react';
import {
    Card,
    CardActions,
    IconButton,
    Typography,
    Container,
    CardMedia,
    Tooltip
} from '@mui/material';
import {deleteKB, getKBsSharedWithMe, listKBs, unshareKBWithMe} from "../API/KB/KBAPI.js";
import {decryptKBItem, getKBKey, setKBKey} from "../App/cryptoUtils.js";
import {Delete, Settings, Add} from '@mui/icons-material';
import {Box} from '@mui/material';
import {colorIcon, colorText} from "../App/theme.js";
import LocalLoader from "../Loaders/LocalLoader.js";
import {KBImageComponent} from "./KBImage.js";
import {AlertContext} from "../App/App.js";
import {
    Menu,
    MenuItem,
} from "@mui/material";
import {MoreVert} from "@mui/icons-material";
import Remove from '@mui/icons-material/RemoveCircleOutline';
import {getAPICache} from "../API/Cache.js";
import {isMobile, kbIdToSubdomainUrl, getParkedDomain} from "../App/utils";
import {Link, useNavigate} from "react-router-dom";
import {KB_TABS} from "../App/constants";
import {updateRecentApps} from "../utils";
import {KBName, KBNamePlural} from "../names";
import {useConfirm} from "../Components/ConfirmContext";
import {insecureSecretWarning} from "../Code/SecretsList";

const KBList = ({sharedWithMe = false, hideIfEmpty, onSuccess, userProfile}) => {
    const [kbs, setKBs] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const {systemAlert, setSystemAlert} = useContext(AlertContext);
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedKb, setSelectedKb] = useState(null);
    const showConfirm = useConfirm();
    const navigate = useNavigate();

    const handleMenuClick = (event, kb) => {
        setSelectedKb(kb);
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    useEffect(() => {
        setKBs([]);

        const fetchKBs = async () => {
            setIsLoading(true);
            try {
                if (sharedWithMe) {
                    const cache = getAPICache('getKBsSharedWithMe');
                    if (cache) setKBs(cache);
                    const kbsSharedData = await getKBsSharedWithMe();
                    if (onSuccess) onSuccess(kbsSharedData)
                    kbsSharedData.forEach(kb => {
                        setKBKey(kb.kbId, kb.key)
                    });
                    setKBs(kbsSharedData.map(kb => ({...kb, kbTitleDecrypted: decryptKBItem(kb.kbTitle, kb.kbId)})))
                } else {
                    const cache = getAPICache('listKBs');
                    if (cache) setKBs(cache);
                    const kbsData = await listKBs();
                    if (onSuccess) onSuccess(kbsData)
                    kbsData.forEach(kb => {
                        setKBKey(kb.kbId, kb.key)
                    });

                    setKBs(kbsData.map(kb => ({...kb, kbTitleDecrypted: decryptKBItem(kb.kbTitle, kb.kbId)})))
                }

            } catch (error) {
                console.error('Error fetching apps:', error);
            }
            setIsLoading(false);
        };

        fetchKBs();
    }, [sharedWithMe]);

    const handleDelete = async (kb) => {
        const confirmation = await showConfirm({
            title: `Delete ${KBName} Confirmation`,
            content: `Are you sure you want to delete "${decryptKBItem(kb.kbTitle, kb.kbId)}"?`
        });

        if (confirmation) {
            onDelete(kb.kbId);
        }
    };

    const handleRemoveAccess = async (kb) => {
        const confirmation = await showConfirm({
            title: 'Revoke Access Confirmation',
            content:  `Are you sure you want to revoke your access to "${decryptKBItem(kb.kbTitle, kb.kbId)}"?`
        });

        if (confirmation) {
            onRemoveAccess(kb.kbId);
        }
    };

    const onRemoveAccess = async (kbId) => {
        setIsLoading(true);
        try {
            await unshareKBWithMe(kbId);
            localStorage.removeItem(`aesKey-${kbId}`);
            setKBs(kbs.filter((kb) => kb.kbId !== kbId));
        } catch (error) {
            if (error?.response?.data?.error) alert(error.response.data.error);
            console.error('Error deleting kb:', error);
        }
        setIsLoading(false);
    };

    const onDelete = async (kbId) => {
        setIsLoading(true);
        try {
            const {hasItems} = await deleteKB(kbId);

            if (hasItems) {
                setSystemAlert({ msg : `Cannot delete ${KBName} ${kbId} because there are related items or chats. Please delete related entities and try again.`, type: 'error'});
                setIsLoading(false);
                return;
            }

            localStorage.removeItem(`aesKey-${kbId}`);
            setKBs(kbs.filter((kb) => kb.kbId !== kbId));
        } catch (error) {
            if (error?.response?.data?.error) alert(error.response.data.error);
            console.error('Error deleting kb:', error);
        }
        setIsLoading(false);
    };

    if (hideIfEmpty && !kbs?.length) return null;

    const iconWidth = isMobile ? '28%' : '140px';

    const WHITELABEL_KB_ID = 'i8c8xk1s74pq';
    const projects = kbs.filter(kb => kb.publishedKbId === WHITELABEL_KB_ID);
    const agents = kbs.filter(kb => kb.publishedKbId !== WHITELABEL_KB_ID);

    const renderCard = (kb) => {
        const KBsubdomainUrl = kbIdToSubdomainUrl(kb.kbId, sharedWithMe);
        const isProject = kb.publishedKbId === WHITELABEL_KB_ID;

        return (
            <Box
                sx={{
                    width: iconWidth,
                    margin: isMobile ? '2%' : '1.8%',
                    padding: isMobile ? '0' : '0'
                }}
                key={kb.kbId} >
                <Tooltip title={isProject ? kb.kbTitleDecrypted : `Start Chat with ${kb.kbTitleDecrypted}`} placement={'top-start'} arrow>
                    <Card
                        onClick={() => updateRecentApps(kb.kbId, kb.kbTitleDecrypted)}
                        style={{
                            cursor: 'pointer',
                            width: '100%',
                            aspectRatio: '1 / 1',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            position: 'relative',
                            transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                        }}
                        sx={{
                            '&:hover': {
                                boxShadow: '0 0 15px rgba(255, 255, 255, 0.5)',
                            }
                        }}
                    >
                        <a href={KBsubdomainUrl}
                           target={isProject ? '_blank' : undefined}
                           style={{ textDecoration: 'none', width: '100%', height: '100%' }}>
                            <CardMedia
                                component={KBImageComponent}
                                alt={kb.kbTitleDecrypted}
                                id={kb.publishedKbId || kb.kbId}
                                width="100%"
                                height="100%"
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'contain'
                                }}
                            />
                        </a>
                        <CardActions style={{position: 'absolute', top: -5, right: 5}}>
                            <IconButton
                                edge="end"
                                aria-label="menu"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleMenuClick(e, kb);
                                }}
                                sx={{
                                    fontSize: isMobile ? '1rem' : 'inherit',
                                    padding: isMobile ? '3px' : 'inherit'
                                }}
                            >
                                <MoreVert sx={isMobile ? { fontSize: '1.25rem'} : {}} />
                            </IconButton>
                            <Menu
                                anchorEl={anchorEl}
                                open={Boolean(anchorEl) && selectedKb === kb}
                                onClose={handleMenuClose}
                            >
                                {!isProject && <MenuItem
                                    component={Link}
                                    to={`${KBsubdomainUrl}?tab=` + KB_TABS.configuration}
                                    onClick={handleMenuClose}
                                >
                                    <Settings style={{color: colorIcon}} fontSize="small" />
                                    <span style={{ textTransform: 'capitalize', paddingLeft: 8 }}>{KB_TABS.configuration}</span>
                                </MenuItem>}
                                {isProject && <MenuItem
                                    component="a"
                                    href={`${KBsubdomainUrl}?noRedirect=1`}
                                    onClick={handleMenuClose}
                                >
                                    <Settings style={{color: colorIcon}} fontSize="small" />
                                    <span style={{ paddingLeft: 8 }}>Dashboard</span>
                                </MenuItem>}
                                {!isProject && <MenuItem
                                    onClick={() => {
                                        sharedWithMe ? handleRemoveAccess(kb) : handleDelete(kb);
                                        handleMenuClose();
                                    }}
                                >
                                    {sharedWithMe
                                        ? <Remove style={{color: colorIcon}} fontSize="small" />
                                        : <Delete style={{color: colorIcon}} fontSize="small" />}

                                    <span style={{paddingLeft: 8}}>{sharedWithMe ? 'Revoke my access' : 'Delete'}</span>
                                </MenuItem>}
                            </Menu>
                        </CardActions>
                    </Card>
                </Tooltip>
                <a href={KBsubdomainUrl} target="_blank" style={{ textDecoration: 'none', width: '100%', height: '100%' }}>
                    <Typography variant="subtitle1" align="center"
                                style={{
                                    marginTop: isMobile ? 4 : 8,
                                    color: colorText,
                                    cursor: 'pointer',
                                    fontSize: isMobile ? 12 : 'inherit',
                                    fontWeight: 400,
                                }} >
                        {isProject ? (kb.domain || kb.kbTitleDecrypted) : kb.kbTitleDecrypted}
                    </Typography>
                </a>
            </Box>
        );
    };

    return (
        <Container key={sharedWithMe ? 1 : 2}>
            <LocalLoader open={isLoading}/>

            {projects.length > 0 && (
                <>
                    <Typography variant="overline" display="block"
                                style={{color: colorText, opacity: 0.5, fontSize: 12, letterSpacing: 2, marginTop: 20, marginBottom: 12}}>
                        {sharedWithMe ? 'Shared Projects' : 'Projects'}
                    </Typography>
                    <Box display="flex" flexWrap="wrap" justifyContent={"flex-start"}>
                        {!sharedWithMe && !getParkedDomain() && (
                            <Box
                                sx={{
                                    width: iconWidth,
                                    margin: isMobile ? '2%' : '1.8%',
                                }}
                            >
                                <Tooltip title="Create New Project" placement="top-start" arrow>
                                    <Card
                                        onClick={() => { alert('Creating new projects is temporarily disabled.'); return; }}
                                        style={{
                                            cursor: 'not-allowed',
                                            width: '100%',
                                            aspectRatio: '1 / 1',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                                            opacity: 0.35,
                                            pointerEvents: 'auto',
                                        }}
                                        sx={{
                                            '&:hover': {
                                                boxShadow: 'none',
                                            }
                                        }}
                                    >
                                        <Add sx={{ fontSize: 48, opacity: 0.4 }} />
                                    </Card>
                                </Tooltip>
                                <Typography variant="subtitle1" align="center"
                                    style={{
                                        marginTop: isMobile ? 4 : 8,
                                        color: colorText,
                                        fontSize: isMobile ? 12 : 'inherit',
                                        fontWeight: 400,
                                        opacity: 0.5,
                                    }}>
                                    New Project
                                </Typography>
                            </Box>
                        )}
                        {projects.map(renderCard)}
                    </Box>
                </>
            )}

            {agents.length > 0 && (
                <>
                    <Typography variant="overline" display="block"
                                style={{color: colorText, opacity: 0.5, fontSize: 12, letterSpacing: 2, marginTop: 20, marginBottom: 12}}>
                        {sharedWithMe ? 'Shared with me' : 'My ' + KBNamePlural}
                    </Typography>
                    <Box display="flex" flexWrap="wrap" justifyContent={"flex-start"}>
                        {agents.map(renderCard)}
                    </Box>
                </>
            )}
        </Container>
    );
};

export default KBList;